import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, Transaction } from '../App';
import { toast } from 'sonner';
import { Trophy, Calendar, Clock, Newspaper, X, Gift } from 'lucide-react';
import { PaymentModal } from './PaymentModal';
import { NewsSection } from './NewsSection';
import { i18n, tb } from '../utils/i18n';
import {
  formatLocaleDate,
  localizeEventTitle,
  localizeEventDescription,
  localizePrizeLabel,
  formatWinnerCount,
  formatPrizeAmount,
  tr,
} from '../utils/localeFormat';

const formatEventDate = (date: Date) => formatLocaleDate(date);

function computeDefaultApiBase() {
  const envValue = (import.meta as any)?.env?.VITE_API_URL?.trim?.();
  if (envValue) {
    return envValue.endsWith('/') ? envValue.slice(0, -1) : envValue;
  }

  if (typeof window === 'undefined') {
    return 'https://wallet.handy.app';
  }

  try {
    const url = new URL(window.location.origin);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.hostname = 'localhost';
      url.port = '3600';
      url.protocol = 'http:';
      return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
    }
    return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
  } catch (error) {
    return 'https://wallet.handy.app';
  }
}

interface EventsProps {
  user: User;
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
  eventSettings: {
    startDate: string;
    endDate: string;
  };
  onTabChange?: (tab: string) => void;
}

export function Events({ user, onUpdateBalance, onAddTransaction, eventSettings, onTabChange }: EventsProps) {
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const resolvedApiBase = useMemo(() => {
    const envValue = (import.meta as any)?.env?.VITE_API_URL?.trim?.() ?? '';
    if (envValue) {
      return envValue.endsWith('/') ? envValue.slice(0, -1) : envValue;
    }
    const fallback = computeDefaultApiBase();
    return fallback.endsWith('/') ? fallback.slice(0, -1) : fallback;
  }, []);

  const buildUrl = useCallback(
    (path: string) => (resolvedApiBase ? `${resolvedApiBase}${path}` : path),
    [resolvedApiBase]
  );

  const [activeTab, setActiveTab] = useState('news');
  const [showPayment, setShowPayment] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [eventStatus, setEventStatus] = useState<'upcoming' | 'active' | 'ended'>('upcoming');
  const [hasParticipated, setHasParticipated] = useState(false);
  const [checkingParticipation, setCheckingParticipation] = useState(true);
  const [currentEvents, setCurrentEvents] = useState<any[]>([]);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [showInsufficientBalancePopup, setShowInsufficientBalancePopup] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [localBalance, setLocalBalance] = useState<number | null>(null);
  
  // user.balance와 localBalance 동기화
  useEffect(() => {
    if (user?.balance !== undefined) {
      setLocalBalance(user.balance);
    }
  }, [user?.balance]);
  
  // 실제 사용할 잔액 (localBalance가 있으면 우선 사용, 없으면 user.balance)
  const currentBalance = localBalance !== null ? localBalance : (user?.balance || 0);

  // 현재 이벤트들 가져오기
  useEffect(() => {
    const fetchCurrentEvents = async () => {
      try {
        const response = await fetch(buildUrl('/api/admin/boom-events/current'));
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.events) {
            setCurrentEvents(data.events);
          } else if (data.success && data.event) {
            // 하위 호환성: 단일 이벤트만 있는 경우
            setCurrentEvents([data.event]);
          }
        }
      } catch (error) {
        console.error('이벤트 정보 조회 실패:', error);
      } finally {
        setLoadingEvent(false);
      }
    };
    fetchCurrentEvents();
  }, [buildUrl]);

  // 첫 번째 이벤트를 currentEvent로 사용 (하위 호환성)
  const currentEvent = useMemo(() => {
    return currentEvents.length > 0 ? currentEvents[0] : null;
  }, [currentEvents]);

  const EVENT_START_TIME = currentEvent ? new Date(currentEvent.start_time).getTime() : 0;
  const EVENT_END_TIME = currentEvent ? new Date(currentEvent.end_time).getTime() : 0;
  const ENTRY_FEE = currentEvent ? Number(currentEvent.entry_fee) : 0;
  const EVENT_ID = currentEvent ? currentEvent.id : 0;

  // 이벤트 페이지 진입 시 잔액 부족 팝업 표시 제거 (이벤트 참여 버튼 밑에 100토큰 받기 버튼이 있으므로)
  // useEffect(() => {
  //   if (activeTab === 'events' && !loadingEvent && currentEvent && eventStatus === 'active') {
  //     const hasSeenInsufficientPopup = sessionStorage.getItem('handy-insufficient-balance-popup-seen');
  //     if (!hasSeenInsufficientPopup && currentBalance < ENTRY_FEE) {
  //       setShowInsufficientBalancePopup(true);
  //     }
  //   }
  // }, [activeTab, loadingEvent, currentEvent, eventStatus, currentBalance, ENTRY_FEE]);

  const eventStartDateDisplay = useMemo(
    () => currentEvent ? formatEventDate(new Date(currentEvent.start_time)) : '',
    [currentEvent]
  );
  const eventEndDateDisplay = useMemo(
    () => currentEvent ? formatEventDate(new Date(currentEvent.end_time)) : '',
    [currentEvent]
  );
  
  // 시작/종료 시간 (시:분 형식)
  const eventStartTimeDisplay = useMemo(() => {
    if (!currentEvent?.start_time) return '';
    const date = new Date(currentEvent.start_time);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period} (KST)`;
  }, [currentEvent]);

  const eventEndTimeDisplay = useMemo(() => {
    if (!currentEvent?.end_time) return '';
    const date = new Date(currentEvent.end_time);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period} (KST)`;
  }, [currentEvent]);

  useEffect(() => {
    const fetchParticipation = async () => {
      if (!currentEvent) {
        setCheckingParticipation(false);
        return;
      }

      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        setCheckingParticipation(false);
        setHasParticipated(false);
        return;
      }

      try {
        const response = await fetch(buildUrl(`/api/boom/my-participation?eventId=${currentEvent.id}`), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setHasParticipated(Boolean(data?.hasParticipated));
        } else {
          setHasParticipated(false);
        }
      } catch (error) {
        console.error('이벤트 참여 여부 조회 실패:', error);
        setHasParticipated(false);
      } finally {
        setCheckingParticipation(false);
      }
    };

    fetchParticipation();
  }, [buildUrl, user?.walletAddress, currentEvent]);

  useEffect(() => {
    // 기본은 뉴스 탭. hash로 명시적으로 #events를 지정한 경우에만 이벤트 탭을 연다.
    // (이전엔 hash가 없으면 무조건 events로 덮어써서 뉴스가 기본이 되지 못했음)
    const handleHashChange = () => {
      if (window.location.hash === '#events') {
        setActiveTab('events');
      } else {
        setActiveTab('news');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (!currentEvent) return;

    // DB의 status를 우선 사용
    const dbStatus = currentEvent.status || currentEvent.event_phase || 'upcoming';
    
    const updateCountdown = () => {
      const now = Date.now();

      // DB 상태 우선 (Admin에서 수동 제어)
      if (dbStatus === 'upcoming' || dbStatus === 'pending') {
        setEventStatus('upcoming');
        const distance = EVENT_START_TIME - now;
        if (distance > 0) {
          setTimeLeft({
            days: Math.floor(distance / (1000 * 60 * 60 * 24)),
            hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((distance % (1000 * 60)) / 1000)
          });
        }
        return;
      }

      if (dbStatus === 'active' || dbStatus === 'ongoing') {
        setEventStatus('active');
        const distance = EVENT_END_TIME - now;
        if (distance > 0) {
          setTimeLeft({
            days: Math.floor(distance / (1000 * 60 * 60 * 24)),
            hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((distance % (1000 * 60)) / 1000)
          });
        }
        return;
      }

      if (dbStatus === 'ended' || dbStatus === 'completed') {
        setEventStatus('ended');
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      // Fallback: 시간 기반 계산
      if (now < EVENT_START_TIME) {
        setEventStatus('upcoming');
        const distance = EVENT_START_TIME - now;
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
        return;
      }

      if (now >= EVENT_START_TIME && now <= EVENT_END_TIME) {
        setEventStatus('active');
        const distance = EVENT_END_TIME - now;
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
        return;
      }

      setEventStatus('ended');
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [currentEvent, EVENT_START_TIME, EVENT_END_TIME]);

  const handleBoomPreRegistration = () => {
    if (checkingParticipation) {
      toast.info(i18n.t('checkingParticipationToast'));
      return;
    }
    if (eventStatus !== 'active') {
      toast.error(eventStatus === 'upcoming' ? i18n.t('eventNotStarted') : i18n.t('eventAlreadyEnded'));
      return;
    }
    if (hasParticipated) {
      toast.error(i18n.t('alreadyParticipatedToast'));
      return;
    }
    if (currentBalance < ENTRY_FEE) {
      setShowInsufficientBalancePopup(true);
      return;
    }
    setShowPayment(true);
  };

  const handleReferralSubmit = async () => {
    if (!referralCode.trim()) {
      toast.error(i18n.t('enterReferralCodeToast'));
      return;
    }

    if (isVerifying) {
      return; // 이미 인증 중이면 무시
    }

    setIsVerifying(true);

    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token || !user?.id) {
        toast.error(i18n.t('authRequired'));
        setIsVerifying(false);
        return;
      }

      // 1. 추천코드 검증
      const API_URL = (import.meta as any).env?.VITE_API_URL || '';
      const validateResponse = await fetch(`${API_URL}/api/referrals/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ referralCode: referralCode.trim().toUpperCase() })
      });

      if (!validateResponse.ok) {
        const responseText = await validateResponse.text();
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || '추천코드 검증 실패');
        } catch (parseError) {
          throw new Error(`서버 오류: ${validateResponse.status} - ${responseText}`);
        }
      }

      const responseText = await validateResponse.text();
      const validateResult = JSON.parse(responseText);
      
      if (!validateResult.valid) {
        toast.error(i18n.t('invalidReferralCodeToast'));
        setIsVerifying(false);
        return;
      }

      // 2. 웰컴 보너스 실제 지급 (블록체인 전송)
      const applyResponse = await fetch(`${API_URL}/api/referrals/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          userId: user.id, 
          referralCode: referralCode.trim().toUpperCase() 
        })
      });

      if (!applyResponse.ok) {
        const responseText = await applyResponse.text();
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || '웰컴 보너스 지급 실패');
        } catch (parseError) {
          throw new Error(`서버 오류: ${applyResponse.status} - ${responseText}`);
        }
      }

      const applyResponseText = await applyResponse.text();
      const applyResult = JSON.parse(applyResponseText);
      
      // 3. 잔액 즉시 업데이트 (3000 wHANDY 추가)
      const balanceBefore = currentBalance;
      const newBalance = balanceBefore + 100;
      
      // 로컬 잔액 즉시 업데이트
      setLocalBalance(newBalance);
      onUpdateBalance(newBalance);
      
      // 4. 모달 닫기 및 UI 업데이트
      setShowReferralModal(false);
      setShowInsufficientBalancePopup(false);
      setReferralCode('');
      
      // 잔액 부족 팝업 표시 플래그 제거 (다시 표시 가능하도록)
      sessionStorage.removeItem('handy-insufficient-balance-popup-seen');
      
      // 5. 백그라운드에서 실제 블록체인 잔액 확인 (트랜잭션 처리 시간 고려)
      setTimeout(async () => {
        try {
          const balanceRes = await fetch(`${API_URL}/api/blockchain/vttn/balance/${user.walletAddress}`);
          if (balanceRes.ok) {
            const balanceData = await balanceRes.json();
            const actualBalance = parseFloat(balanceData.balance);
            // 실제 잔액으로 업데이트
            setLocalBalance(actualBalance);
            onUpdateBalance(actualBalance);
          }
        } catch (err) {
          console.warn('잔액 확인 실패:', err);
        }
      }, 2000);
      
      toast.success(tr('welcomeBonusReceivedToast', { tx: applyResult.txHash?.slice(0, 10) || '' }));
      
    } catch (error: any) {
      console.error('추천코드 처리 오류:', error);
      
      // 이미 받은 경우 특별 처리
      if (error.message?.includes('이미 추천코드를 적용했습니다') || 
          error.message?.includes('이미 웰컴 보너스를 수령했습니다')) {
        toast.info(i18n.t('alreadyReceivedBonus'));
        setShowReferralModal(false);
        setShowInsufficientBalancePopup(false);
        setReferralCode('');
      } else {
        toast.error(error.message || i18n.t('referralProcessingError'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setHasParticipated(true);
    setShowPayment(false);

    try {
      const newBalance = Math.max((user.balance ?? 0) - ENTRY_FEE, 0);
      await onUpdateBalance(newBalance);

      const newTransaction = {
        type: '1st BOOM Event Pre-Registration',
        amount: -ENTRY_FEE,
        createdAt: new Date().toISOString()
      };
      await onAddTransaction(newTransaction);
    } catch (error) {
      console.error('이벤트 결제 후 업데이트 오류:', error);
    }
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="bg-background border-b border-border sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex space-x-1 bg-muted rounded-xl p-1 w-full">
            <button
              onClick={() => setActiveTab('events')}
              className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
                activeTab === 'events'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Trophy className="w-4 h-4" />
                <span className="font-medium">Events</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('news')}
              className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
                activeTab === 'news'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Newspaper className="w-4 h-4" />
                <span className="font-medium">News</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {activeTab === 'events' ? (
          // 이벤트 기능 준비중 - 실제 이벤트 참여 로직 완성 전까지 안내만 표시 (2026-09-10)
          true ? (
            <div className="flex items-center justify-center h-full text-center p-8">
              <div>
                <Trophy className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                <p className="text-lg font-semibold text-foreground mb-2">이벤트 준비중입니다</p>
                <p className="text-sm text-muted-foreground">곧 새로운 이벤트로 찾아뵙겠습니다.</p>
              </div>
            </div>
          ) : loadingEvent ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
                <p>{i18n.t('loadingEvents')}</p>
              </div>
            </div>
          ) : !currentEvent ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <p>{i18n.t('noActiveEvents')}</p>
              </div>
            </div>
          ) : (
          <div className="p-4 space-y-6">
            <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0 shadow-2xl">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <Trophy className="h-8 w-8 text-yellow-300" />
                    <div>
                      <h1 className="text-2xl font-bold">
                        {localizeEventTitle(currentEvent?.title || i18n.t('eventDefaultTitle'), eventStatus)}
                        {eventStatus && eventStatus !== 'ended' && (
                          <span className={`ml-2 text-sm px-3 py-1 rounded-full ${
                            eventStatus === 'upcoming' ? 'bg-blue-500/30 text-blue-100' :
                            eventStatus === 'active' ? 'bg-green-500/30 text-green-100' :
                            'bg-gray-500/30 text-gray-100'
                          }`}>
                            {eventStatus === 'upcoming' ? i18n.t('eventStatusUpcoming') :
                             eventStatus === 'active' ? i18n.t('eventStatusActive') :
                             i18n.t('eventStatusEnded')}
                          </span>
                        )}
                      </h1>
                  <p className="text-white/90">{localizeEventDescription(currentEvent?.description || '')}</p>
                    </div>
                    <Trophy className="h-8 w-8 text-yellow-300" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/20 rounded-2xl p-4 text-center backdrop-blur-sm">
                    <p className="text-white/80 text-sm mb-1">{i18n.t('yourBalance')}</p>
                    <p className="text-2xl font-bold">{currentBalance.toFixed(2)}</p>
                    <p className="text-white/80 text-sm">wHANDY</p>
                  </div>
                  <div className="bg-white/20 rounded-2xl p-4 text-center backdrop-blur-sm">
                    <p className="text-white/80 text-sm mb-1">{i18n.t('entryFee')}</p>
                    <p className="text-2xl font-bold">{ENTRY_FEE || 0}</p>
                    <p className="text-white/80 text-sm">wHANDY</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                      <Calendar className="h-5 w-5 text-white mx-auto mb-1" />
                      <p className="text-white/80 text-xs mb-1">{i18n.t('eventStartLabel')}</p>
                      <p className="text-white font-bold text-sm">{eventStartDateDisplay}</p>
                      <p className="text-white/70 text-xs">{eventStartTimeDisplay}</p>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                      <Clock className="h-5 w-5 text-white mx-auto mb-1" />
                      <p className="text-white/80 text-xs mb-1">{i18n.t('eventEndLabel')}</p>
                    <p className="text-white font-bold text-sm">{eventEndDateDisplay}</p>
                    <p className="text-white/70 text-xs">{eventEndTimeDisplay}</p>
                    </div>
                  </div>

                  <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                    <div className="flex items-center justify-center space-x-2 mb-4">
                      <Trophy className="h-5 w-5 text-yellow-300" />
                      <p className="text-white font-bold">{i18n.t('totalPrizePoolLabel')} {currentEvent?.reward_pool ? Number(currentEvent.reward_pool).toLocaleString() : 0} wHANDY</p>
                    </div>
                    {currentEvent?.prize_distribution?.distribution && currentEvent.prize_distribution.distribution.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-center text-white/90 text-sm">
                        {currentEvent.prize_distribution.distribution.map((prize: any, index: number) => {
                          const bgColors = ['bg-yellow-400/20', 'bg-orange-400/20', 'bg-purple-400/20', 'bg-pink-400/20', 'bg-blue-400/20'];
                          const textColors = ['text-yellow-200', 'text-orange-200', 'text-purple-200', 'text-pink-200', 'text-blue-200'];
                          const bgColor = bgColors[index % bgColors.length];
                          const textColor = textColors[index % textColors.length];
                          const isLast = index === currentEvent.prize_distribution.distribution.length - 1;
                          
                          return (
                            <div 
                              key={prize.rank} 
                              className={`${bgColor} rounded-lg p-3 ${isLast && currentEvent.prize_distribution.distribution.length % 2 !== 0 ? 'md:col-span-2' : ''}`}
                            >
                              <p className="font-semibold text-white text-base">
                                {localizePrizeLabel(prize.label)} {formatWinnerCount(prize.count)}
                              </p>
                              <p className={`${textColor} text-lg font-bold`}>
                                {formatPrizeAmount(Number(prize.prize), prize.count)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-white/70 text-center text-sm">{i18n.t('prizeInfoUnavailable')}</p>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-white/90 text-sm mb-4">
                    {eventStatus === 'upcoming' && '🚨 Event starts in:'}
                    {eventStatus === 'active' && '⚡ Event ends in:'}
                    {eventStatus === 'ended' && '🏁 Event has ended'}
                  </p>

                  {eventStatus !== 'ended' && (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                        <div className="text-3xl font-bold">{timeLeft.days}</div>
                        <div className="text-white/80 text-xs">DAYS</div>
                      </div>
                      <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                        <div className="text-3xl font-bold">{timeLeft.hours}</div>
                        <div className="text-white/80 text-xs">HOURS</div>
                      </div>
                      <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                        <div className="text-3xl font-bold">{timeLeft.minutes}</div>
                        <div className="text-white/80 text-xs">MIN</div>
                      </div>
                      <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                        <div className="text-3xl font-bold">{timeLeft.seconds}</div>
                        <div className="text-white/80 text-xs">SEC</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Button
                onClick={handleBoomPreRegistration}
                className="w-full h-16 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-xl rounded-2xl shadow-xl transition-all duration-300"
                disabled={
                  checkingParticipation ||
                  currentBalance < ENTRY_FEE ||
                  eventStatus !== 'active' ||
                  hasParticipated
                }
              >
                {checkingParticipation ? (
                  <span>CHECKING STATUS...</span>
                ) : eventStatus === 'upcoming' ? (
                  <div className="text-center">
                    <div className="font-bold">{i18n.t('eventPreparing')}</div>
                    <div className="text-sm">{i18n.t('eventStartingSoon')}</div>
                  </div>
                ) : eventStatus === 'ended' ? (
                  <div className="text-center">
                    <div className="font-bold">{i18n.t('eventEndedButton')}</div>
                    <div className="text-sm">{i18n.t('winnerAnnouncementPending')}</div>
                  </div>
                ) : hasParticipated ? (
                  <span>{i18n.t('alreadyParticipated')}</span>
                ) : (
                  <span>{tr('joinEventButton', { fee: ENTRY_FEE })}</span>
                )}
              </Button>

              {hasParticipated && (
                <div className="bg-green-500/20 border-2 border-green-400 rounded-xl p-4 text-center text-green-100">
                  <p className="font-semibold">{i18n.t('alreadyJoinedEvent')}</p>
                  <p className="text-sm mt-1">{i18n.t('checkTransactionHistory')}</p>
                </div>
              )}

              {currentBalance < ENTRY_FEE && eventStatus !== 'ended' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600 rounded-xl p-4 text-center space-y-3">
                  <p className="text-blue-700 dark:text-blue-300 font-semibold">{tr('insufficientEntryFee', { fee: ENTRY_FEE })}</p>
                  <Button
                    onClick={() => {
                      setShowReferralModal(true);
                      setShowInsufficientBalancePopup(true);
                    }}
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    {i18n.t('enterReferralGet100')}
                  </Button>
                </div>
              )}
              {currentBalance < ENTRY_FEE && eventStatus === 'ended' && (
                <div className="bg-gray-50 dark:bg-gray-900/20 border-2 border-gray-300 dark:border-gray-600 rounded-xl p-4 text-center space-y-3 opacity-50">
                  <p className="text-gray-500 dark:text-gray-400 font-semibold">{tr('insufficientEntryFee', { fee: ENTRY_FEE })}</p>
                  <Button
                    disabled
                    className="w-full h-12 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold rounded-xl shadow-lg cursor-not-allowed"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    {i18n.t('enterReferralGet100')}
                  </Button>
                </div>
              )}
            </div>
          </div>
          )
        ) : (
          <NewsSection user={user} />
        )}
      </div>

      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        amount={ENTRY_FEE}
        currency="wHANDY"
        title="1st BOOM Event Registration"
        description="Join the biggest tournament in Handy history!"
        onSuccess={handlePaymentSuccess}
        apiBase={resolvedApiBase}
        eventId={EVENT_ID}
        userBalance={user.balance}
      />

      {/* 잔액 부족 팝업 */}
      {showInsufficientBalancePopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">{i18n.t('insufficientBalanceTitle')}</h2>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowInsufficientBalancePopup(false);
                  sessionStorage.setItem('handy-insufficient-balance-popup-seen', 'true');
                }}
                className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 text-center">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <span className="font-bold text-lg">{tr('insufficientBalanceMessage', { nickname: user?.nickname || i18n.t('memberDefault') })}</span>
                </p>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {tb('insufficientBalanceHint')}
                </p>
              </div>

              <Button
                onClick={() => {
                  setShowReferralModal(true);
                }}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg"
              >
                <Gift className="w-4 h-4 mr-2" />
                {i18n.t('enterReferralGet100')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 추천코드 입력 모달 */}
      {showReferralModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Welcome Bonus</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{i18n.t('referralModalDesc')}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Input
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder={i18n.t('enterReferralCode')}
                  className="h-12 text-lg font-semibold border-2 focus:border-green-500 text-center uppercase tracking-wider"
                  autoFocus
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => {
                    setShowReferralModal(false);
                    setReferralCode('');
                  }}
                  variant="outline"
                  className="flex-1 h-12 text-base font-semibold"
                  disabled={isVerifying}
                >
                  {i18n.t('cancel')}
                </Button>
                <Button
                  onClick={handleReferralSubmit}
                  disabled={!referralCode.trim() || isVerifying}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold"
                >
                  {isVerifying ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{i18n.t('verifying')}</span>
                    </div>
                  ) : (
                    i18n.t('verify')
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}